// Sticky exam header: a way out, progress + time + the board/grade the student
// works in.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { boardGradeLabel, decodeClaims } from '@/lib/auth';
import { readGarden } from '@/hooks/useGarden';
import { Icon } from '@/components/garden';
import type { TimerMode } from '@/types';

interface ExamHeaderProps {
  total: number;
  attempted: number;
  submitted: number;
  elapsedSeconds: number;
  totalSeconds: number;
  timerMode: TimerMode;
  /** Fired once in ENFORCED mode when elapsed reaches totalSeconds. */
  onTimeUp?: () => void;
}

function fmt(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function ExamHeader({
  total,
  attempted,
  submitted,
  elapsedSeconds,
  totalSeconds,
  timerMode,
  onTimeUp,
}: ExamHeaderProps) {
  const label = useMemo(() => boardGradeLabel(decodeClaims()), []);
  const streak = useMemo(() => readGarden().streak, []);
  const remaining = Math.max(0, total - attempted);
  const progressPct = total > 0 ? Math.min(100, Math.round((attempted / total) * 100)) : 0;
  const timePct = totalSeconds > 0 ? Math.min(100, (elapsedSeconds / totalSeconds) * 100) : 0;
  const timeWarning = timePct >= 80;

  // ENFORCED auto-submit.
  useEffect(() => {
    if (timerMode === 'ENFORCED' && totalSeconds > 0 && elapsedSeconds >= totalSeconds) {
      onTimeUp?.();
    }
  }, [timerMode, elapsedSeconds, totalSeconds, onTimeUp]);

  return (
    <div className="sticky top-0 z-30">
      <LeaveExam attempted={attempted} />
      <header className="border-b border-slate-200 bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:gap-4">
          {/* Left: counts + board/grade */}
          <div className="flex min-w-0 items-center justify-between gap-3 sm:w-44 sm:flex-col sm:items-start sm:justify-center">
            <div className="text-sm font-semibold text-ink">
              {attempted} / {total} attempted
              <span className="ml-2 font-normal text-slate-500">{remaining} remaining</span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span
                className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-white"
                title="Your board and grade (set during onboarding)"
              >
                {label || '—'}
              </span>
              {streak > 0 && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-fs-11 font-bold"
                  style={{ background: 'var(--orange-bg)', color: '#BA5A17' }}
                  title="Finish today's set to keep your streak"
                >
                  <Icon name="flame" size={12} /> streak safe
                </span>
              )}
            </div>
          </div>

          {/* Centre: completion progress */}
          <div className="flex-1">
            <div className="mb-1 flex justify-between text-fs-11 text-slate-500">
              <span>Progress</span>
              <span>{progressPct}%</span>
            </div>
            <div
              className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Paper completion"
            >
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${progressPct}%`, backgroundColor: 'var(--bin-green)' }}
              />
            </div>
            {submitted > 0 && (
              <div className="mt-0.5 text-fs-11 text-slate-400">{submitted} graded</div>
            )}
          </div>

          {/* Right: time bar */}
          <div className="sm:w-44">
            <div className="mb-1 flex justify-between text-fs-11 text-slate-500">
              <span>{timerMode === 'ENFORCED' ? 'Time (enforced)' : 'Time'}</span>
              <span style={timeWarning ? { fontWeight: 700, color: 'var(--danger)' } : undefined}>
                {fmt(elapsedSeconds)} / {fmt(totalSeconds)}
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{
                  width: `${timePct}%`,
                  backgroundColor: timeWarning ? 'var(--danger)' : 'var(--progress-time)',
                }}
              />
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}

/**
 * A way out of the paper that is not "submit".
 *
 * The exam screen had no exit at all: a student who opened the wrong paper, or
 * had to stop, either handed in a half-done paper or left by the browser's back
 * button. Leaving does not submit. Every answered question is already saved as
 * it is graded, so the paper stays in progress and "Resume" brings the student
 * back to it; only an answer typed and not yet submitted is lost, and the
 * confirmation says so.
 */
function LeaveExam({ attempted }: { attempted: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  // A key listener, not a write.
  useEffect(() => {
    if (!confirming) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setConfirming(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming]);

  const leave = () => {
    // Everything fetched before the paper began is now out of date and would be
    // served from cache: the lists would offer "Start" on the paper just left,
    // and Resume would hydrate from the empty session it opened with.
    void queryClient.invalidateQueries({ queryKey: ['papers'] });
    void queryClient.invalidateQueries({ queryKey: ['challenge'] });
    void queryClient.invalidateQueries({ queryKey: ['session'] });
    // Back to wherever the paper was started from — Assigned, Past papers,
    // Today. A paper opened straight from a link has nowhere in the app to go
    // back to.
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate('/tests/assigned', { replace: true });
  };

  return (
    <>
      <div className="border-b border-slate-200 bg-card">
        <div className="mx-auto flex max-w-3xl items-center px-3 py-1.5">
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="text-sm font-semibold text-slate-600 transition-colors duration-150 hover:text-ink"
          >
            ← Leave exam
          </button>
        </div>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          onClick={() => setConfirming(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-card p-5 shadow-pop sm:rounded-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Leave this exam"
          >
            <h3 className="text-lg font-bold text-ink">Leave this exam?</h3>
            <p className="mt-2 text-sm text-slate-600">
              {attempted > 0
                ? `Your ${attempted} answered question${attempted === 1 ? ' is' : 's are'} saved.`
                : 'You haven’t answered anything yet.'}{' '}
              The paper stays open — choose <strong>Resume</strong> on it to carry on where you left
              off.
            </p>
            <p className="mt-2 text-xs text-slate-400">
              An answer you have typed but not submitted won’t be kept. Leaving doesn’t submit the
              paper.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 font-semibold text-slate-700 hover:bg-slate-50"
              >
                Keep going
              </button>
              <button
                type="button"
                onClick={leave}
                className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 font-semibold text-white hover:bg-slate-800"
              >
                Leave exam
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
